import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  Snackbar, Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useSchool } from '../contexts/SchoolContext';
import { getPermissionsForRole, permissionLabels } from '../utils/rolePermissions';

const SchoolStaff: React.FC = () => {
  const { staff, addStaff, updateStaffRole, deleteStaff, loading } = useSchool();
  const [open, setOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'teacher',
    className: ''
  });

  const handleOpen = (staffMember?: any) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        name: staffMember.name,
        email: staffMember.email,
        password: '',
        role: staffMember.role,
        className: staffMember.className || ''
      });
    } else {
      setEditingStaff(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'teacher',
        className: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingStaff(null);
  };

  const handleSubmit = async () => {
    try {
      const trimmedClassName = formData.className.trim();
      if (editingStaff) {
        await updateStaffRole(editingStaff._id, formData.role, trimmedClassName);
        setSnackbarSeverity('success');
        setSnackbarMessage('Staff class and role updated successfully.');
      } else {
        await addStaff({ ...formData, className: trimmedClassName });
        setSnackbarSeverity('success');
        setSnackbarMessage('Staff member added successfully.');
      }
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error('Error saving staff:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to save staff assignment. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (staffId: string) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await deleteStaff(staffId);
      } catch (error) {
        console.error('Error deleting staff:', error);
      }
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Staff Management
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 1 }}>
            Assign a class to teachers so they only see the students in their classroom.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Add Staff
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((member) => (
              <TableRow key={member._id}>
                <TableCell>{member.name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  <Chip
                    label={member.role}
                    color={member.role === 'admin' ? 'primary' : member.role === 'teacher' ? 'secondary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{member.className || '—'}</TableCell>
                <TableCell>
                  {(member.permissions ? Object.entries(member.permissions) : Object.entries(getPermissionsForRole(member.role))).map(
                    ([permission, allowed]) => (
                      <Chip
                        key={permission}
                        label={`${permissionLabels[permission as keyof typeof permissionLabels]}${allowed ? '' : ' (No)'}`}
                        size="small"
                        color={allowed ? 'success' : 'default'}
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    )
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(member)} size="small">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(member._id)} size="small" color="error">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {staff.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          No staff members yet
        </Typography>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStaff ? 'Edit Staff Role' : 'Add New Staff'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={!!editingStaff}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              disabled={!!editingStaff}
            />
            {!editingStaff && (
              <TextField
                label="Password"
                type="password"
                fullWidth
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              />
            )}
            <TextField
              label="Class"
              fullWidth
              value={formData.className}
              onChange={(e) => setFormData(prev => ({ ...prev, className: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="teacher">Teacher</MenuItem>
                <MenuItem value="accountant">Accountant</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Role Permissions
              </Typography>
              {Object.entries(getPermissionsForRole(formData.role)).map(([permission, allowed]) => (
                <Chip
                  key={permission}
                  label={`${permissionLabels[permission as keyof typeof permissionLabels]}${allowed ? '' : ' (No)'}`}
                  size="small"
                  color={allowed ? 'success' : 'default'}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingStaff ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolStaff;